import { useSelector } from '@xstate/react';
import cn from 'classnames';
import { useCallback, useContext, useEffect, useState } from 'react';

import { Checkbox } from '@webapp/common/components/checkbox';
import { CustomStylesCtx } from '@webapp/common/components/custom-styles';
import { GradientNumbers } from '@webapp/common/components/gradient-numbers';
import { NumbersWithBorders } from '@webapp/common/components/numbers-with-borders';
import { Rating } from '@webapp/common/components/rating';
import { Slider } from '@webapp/common/components/slider';
import { CssPageBlock, CssUiComponent } from '@webapp/common/components/survey-custom';
import { getColor, setHtml } from '@webapp/common/lib/ui';
import { CANTRATE_ID } from '@webapp/common/resources/constants';

import type { QuestionAnswerActor } from '@webapp/survey/src/entities/answer-machine';
import { usePipingAnswersReplacer } from '@webapp/survey/src/machine/use-piping-answers-replacer';
import { useStateSelector } from '@webapp/survey/src/machine/use-state-selector';

import { Comment } from '../../components/comment';

import css from './csi.css';

// То что выводится в опросе и в превью просмотре (при редактировании)
export const CsiRow: FC<{
    questionId: number;
    answer: QuestionAnswerActor;
    style: React.CSSProperties;
    labelStyle: React.CSSProperties;
    color: any;
    count: any;
    size: any;
    matrixType: string;
    matrixNumbersColor: Array<string>;
    minAmount: number;
    maxAmount: number;
    csiFormat: string;
    comment?: boolean;
    commentTextHelp?: string;
    showCantRate?: boolean;
    commentRequired?: boolean;
    cantRateText?: string;
}> = ({
    questionId,
    answer: answerActor,
    color,
    matrixNumbersColor,
    size,
    style,
    labelStyle,
    minAmount,
    maxAmount,
    csiFormat,
    comment,
    commentTextHelp,
    showCantRate,
    commentRequired,
    cantRateText,
    matrixType,
    count,
}) => {
    const pipingAnswersReplacer = usePipingAnswersReplacer();
    const answer = useSelector(answerActor, (a) => a.context);
    const {
        response: responseActor,
        valid,
        value: [value1, value2, value3],
        forwardParent,
    } = answer;
    const questions = useStateSelector((s) => s.questions);
    const [forwardParentActor, setForwardParentActor] = useState<undefined | QuestionAnswerActor>();
    const forwardParentContext = useSelector(forwardParentActor, (s) => s?.context);

    const forwardParentValue = useSelector(forwardParentContext?.response, (s) => s?.context.extra);
    useEffect(() => {
        if (!forwardParent && !forwardParentActor) return undefined;

        questions.forEach((q) => {
            const founded = q.getSnapshot().context.answersActors.find((a) => {
                return a.getSnapshot().context.id === forwardParent;
            });
            if (founded) {
                setForwardParentActor(founded);
            }
        });
    }, [questions, forwardParent]);
    const response = useSelector(responseActor, (a) => a.context);
    const commentText = response.comment;
    const cantRate = response.value === CANTRATE_ID;
    // Сделано для обратной совместимости
    const itemsCount = !maxAmount ? count : maxAmount + (minAmount === 0 ? 1 : 0);
    const { textField: customStyles } = useContext(CustomStylesCtx);
    const isAnswerInvalid = valid === false;
    const isCommentInvalid = isAnswerInvalid && commentRequired && !response.comment;

    const onChange = useCallback(
        (v: number): void => {
            responseActor.send({ type: 'CHANGE', value: v, questionId });
        },
        [responseActor, questionId],
    );
    const onChangeComment = useCallback(
        (v: string): void => {
            responseActor.send({ type: 'CHANGE_COMMENT', value: v });
        },
        [responseActor],
    );
    const onChangeExtra = useCallback(
        (v: number): void => {
            responseActor.send({ type: 'CHANGE_EXTRA', value: v });
        },
        [responseActor],
    );

    const handleCantRate = useCallback(
        (checked: boolean) => {
            responseActor.send({ type: 'CHANGE', value: checked ? CANTRATE_ID : undefined, questionId });
            responseActor.send({ type: 'CHANGE_EXTRA', value: undefined });
        },
        [responseActor, questionId],
    );

    const renderInput = (val: string | number | boolean, cb: (val: string | number) => void) => {
        if (csiFormat === 'numbers-with-borders') {
            return (
                <NumbersWithBorders
                    colors={matrixNumbersColor}
                    itemsCount={itemsCount}
                    startsWith={minAmount}
                    selectedValue={val as number}
                    onClick={cb}
                />
            );
        }
        if (csiFormat === 'slider') {
            return (
                <Slider
                    min={minAmount}
                    max={maxAmount}
                    value={typeof val === 'number' ? val : null}
                    withoutControl
                    rangeClassName={css.rangeSlider}
                    labelsBottom
                    trackBgImage={`linear-gradient(90deg, ${matrixNumbersColor.join(', ')})`}
                    onChange={cb}
                />
            );
        }
        // Сделано доп условие для обратной совместимости
        if (csiFormat === 'numbers' || (!csiFormat && matrixType === 'numbers')) {
            return (
                <GradientNumbers
                    size='small'
                    colors={matrixNumbersColor}
                    itemsCount={itemsCount}
                    startsWith={minAmount ?? 1}
                    selectedValue={val as number}
                    onClick={cb}
                />
            );
        }

        return (
            <Rating
                key={String(cantRate)}
                color={color}
                count={itemsCount}
                defaultValue={val as number}
                size={size}
                onChange={cb}
            />
        );
    };
    if (answer.hidden || answer.hiddenByAnswersHide) return null;
    return (
        <>
            <div
                className={cn(CssUiComponent.LABEL, css.label, css.mainLabel)}
                style={{ ...style, ...labelStyle }}
                {...setHtml(pipingAnswersReplacer(forwardParentValue ? forwardParentValue.toString() : value1))}
            />
            <div
                className={cn(CssPageBlock.ANSWER, css.answer, {
                    [css.answer_invalid]: isAnswerInvalid,
                })}
            >
                <div
                    className={cn(CssUiComponent.LABEL, css.label, { [css.answer_disabled]: cantRate })}
                    style={style}
                    {...setHtml(pipingAnswersReplacer(value2))}
                />
                {renderInput(cantRate ? undefined : response.value, onChange)}
            </div>
            <div
                className={cn(CssPageBlock.ANSWER, css.answer, {
                    [css.answer_invalid]: isAnswerInvalid,
                })}
            >
                <div
                    className={cn(CssUiComponent.LABEL, css.label, { [css.answer_disabled]: cantRate })}
                    style={style}
                    {...setHtml(pipingAnswersReplacer(value3))}
                />
                {renderInput(cantRate ? undefined : response.extra, onChangeExtra)}
            </div>
            {comment && (
                <Comment
                    disabled={cantRate}
                    className={css.commentWrap}
                    commentInvalid={!cantRate && isCommentInvalid}
                    commentTextHelp={commentTextHelp}
                    commentText={commentText}
                    showMark={commentRequired}
                    onChange={onChangeComment}
                    markColor={!cantRate && isCommentInvalid ? getColor('red') : customStyles['--c-border-focus']}
                />
            )}
            {showCantRate && (
                <div className={css.checkboxWrap}>
                    <Checkbox checked={cantRate} label={cantRateText} onChange={handleCantRate} />
                </div>
            )}
        </>
    );
};
